/**
 * Reading the help: which topics exist in this build, in which order, found
 * by which words, and the text of a topic as plain strings. Platform-free;
 * the web and the app only render.
 */
import { resolveHelpText } from './facts';
import { HELP_FLAGS, type HelpBlock, type HelpDevice, type HelpGroup, type HelpRole, type HelpTopic } from './model';
import { HELP_TOPICS } from './topics';

export const HELP_GROUPS: readonly HelpGroup[] = ['start', 'tasks', 'roles', 'devices', 'faq'];

/** Topics of this build: those behind a flag that is off are not offered at all. */
export function helpTopics(): readonly HelpTopic[] {
  return HELP_TOPICS.filter((t) => !t.requires || HELP_FLAGS[t.requires]);
}

export function findHelpTopic(id: string | null | undefined): HelpTopic | null {
  if (!id) return null;
  return helpTopics().find((t) => t.id === id) ?? null;
}

/** The device sections in the reader's order (devices.ts: their own first). */
export function deviceTopics(order: readonly HelpDevice[]): HelpTopic[] {
  const byDevice = new Map(helpTopics().filter((t) => t.device).map((t) => [t.device as HelpDevice, t]));
  const devices = order.map((d) => byDevice.get(d)).filter((t): t is HelpTopic => t !== undefined);
  // Device-related topics without a device of their own (notifications) after the sections.
  const extra = helpTopics().filter((t) => t.group === 'devices' && !t.device);
  return [...devices, ...extra];
}

/** Topics meant for a role (for "Für deine Rolle"), in source order. */
export function topicsForRole(role: HelpRole): HelpTopic[] {
  return helpTopics().filter((t) => t.roles?.includes(role) && t.group !== 'devices');
}

export function topicsInGroup(group: HelpGroup): HelpTopic[] {
  return helpTopics().filter((t) => t.group === group);
}

/**
 * The blocks to show on a device: a device-specific block is kept when it is
 * for this device. `null` keeps every block (the reader chose "alle Geräte").
 */
export function blocksFor(blocks: readonly HelpBlock[], device: HelpDevice | null): HelpBlock[] {
  return blocks.filter((b) => !('on' in b) || device === null || b.on.includes(device));
}

/** Every string of a topic, resolved, for searching. */
function textOf(topic: HelpTopic): string {
  const parts: string[] = [];
  const walk = (blocks: readonly HelpBlock[]) => {
    for (const b of blocks) {
      if ('p' in b) parts.push(b.p);
      else if ('h' in b) parts.push(b.h);
      else if ('note' in b) parts.push(b.note);
      else if ('steps' in b) parts.push(...b.steps);
      else if ('list' in b) parts.push(...b.list);
      else walk(b.blocks);
    }
  };
  walk(topic.blocks);
  return parts.map(resolveHelpText).join(' ');
}

/** Lowercase without accents, ß as ss: "Rückfrage" and "ruckfrage" find each other. */
export function foldForSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * Topics matching every word of the query, best first: a word in the title
 * counts most, then keywords, then the summary, then the text. Fewer than two
 * characters find nothing (as the damage search).
 */
export function searchHelp(query: string): HelpTopic[] {
  const words = foldForSearch(query)
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 2);
  if (words.length === 0) return [];
  const scored = helpTopics().map((topic, order) => {
    const title = foldForSearch(resolveHelpText(topic.title));
    const keywords = foldForSearch((topic.keywords ?? []).join(' '));
    const summary = foldForSearch(resolveHelpText(topic.summary));
    const body = foldForSearch(textOf(topic));
    let score = 0;
    for (const w of words) {
      const s = (title.includes(w) ? 8 : 0) + (keywords.includes(w) ? 4 : 0) + (summary.includes(w) ? 2 : 0) + (body.includes(w) ? 1 : 0);
      if (s === 0) return { topic, score: 0, order };
      score += s;
    }
    return { topic, score, order };
  });
  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .map((s) => s.topic);
}

export { resolveHelpText, factKeysOf, helpFacts } from './facts';
export { HELP_FLAGS } from './model';
export type { HelpBlock, HelpDevice, HelpGroup, HelpRole, HelpTopic } from './model';
export { HELP_TOPICS } from './topics';
export { nativeHelpContext, webHelpContext, devicesInOrder, type HelpContext } from './devices';
export { CONTEXT_HELP, type HelpScreen } from './context';
